// PreviewCanvas — the live blank-canvas embroidery visualiser.
//
// Renders the spot the customer is building at true real-world scale on
// an artboard tinted to the chosen garment colour: mm rulers down the
// top + left edges, a dot grid, centre crosshairs, and the artwork
// (uploaded design + text in the real embroidery font + thread colour).
//
// Sizing model: the size slider value is the LONGEST side of the whole
// design. A wide design fills that much width; a tall design fills that
// much height. In "design + text" mode the artwork and text are treated
// as one unit — the text is a caption sized off the artwork, never
// stretched to dominate it. The artboard world is a fixed 340mm x 360mm
// (SVG viewBox in mm, 1 unit = 1 mm).
//
// W4 phase 2: the artwork pipeline (remove-bg preview, drawing filter,
// varsity flags, font/thread resolution, aspect measurement) moved
// verbatim into util/usePreviewArtwork.ts so the on-garment photo
// preview (OnGarmentPreview) derives the identical artwork. The layout
// maths + SVG renderers below are exported for the same reason. This
// component's behaviour is unchanged.

import * as React from "react";
import type { Config, Font } from "../types";
import type { EmbroideryDraft } from "../sections/PlacementSection";
import { usePreviewArtwork } from "../util/usePreviewArtwork";
import { WordArtText } from "./WordArtText";
import { isSleeveSlug } from "../util/sleeve";

export const CANVAS_MM_W = 340;
export const CANVAS_MM_H = 360;
const RULER_PX = 28; // ruler band thickness, CSS px (not mm)

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

// ── Garment colour resolution ──────────────────────────────────────────
// The canvas background is the garment the customer picked. Priority:
//   1. a real hex (variant `custom.colour_hex` metafield, or a theme
//      swatch resolved client-side)
//   2. a name -> hex lookup for common garment colours
//   3. a soft natural fallback so the dark dot grid still reads.

const COLOUR_NAME_MAP: Array<[string, string]> = [
  ["off white", "#f3efe6"],
  ["vintage white", "#efe9dc"],
  ["natural", "#ece3d2"],
  ["ecru", "#e8dfca"],
  ["oatmeal", "#e4dac6"],
  ["bone", "#e9e3d5"],
  ["sand", "#d9c9a8"],
  ["stone", "#cabfa9"],
  ["khaki", "#b6a884"],
  ["army", "#5a5a3c"],
  ["olive", "#6a6a3e"],
  ["charcoal", "#36383b"],
  ["heather grey", "#b6b6b6"],
  ["heather gray", "#b6b6b6"],
  ["light grey", "#cfcfcf"],
  ["light gray", "#cfcfcf"],
  ["dark grey", "#5b5d60"],
  ["dark gray", "#5b5d60"],
  ["navy", "#1b2440"],
  ["royal", "#11468f"],
  ["sky", "#7cccf2"],
  ["maroon", "#5a1f2b"],
  ["burgundy", "#5e2233"],
  ["forest", "#234734"],
  ["bottle", "#1f4636"],
  ["mint", "#bfe3cf"],
  ["teal", "#1f7e80"],
  ["mustard", "#caa12c"],
  ["gold", "#caa53a"],
  ["yellow", "#f4cf3f"],
  ["orange", "#e8743b"],
  ["coral", "#e8765a"],
  ["pink", "#f3a9c6"],
  ["purple", "#5c2a82"],
  ["lavender", "#bcaad6"],
  ["brown", "#5a3c2a"],
  ["chocolate", "#4a3120"],
  ["tan", "#c7a983"],
  ["beige", "#ddcfb6"],
  ["cream", "#f1e9d6"],
  ["white", "#f6f4ef"],
  ["black", "#1a1a1c"],
  ["grey", "#9a9a9c"],
  ["gray", "#9a9a9c"],
  ["silver", "#c4c4c6"],
  ["green", "#2f6b46"],
  ["blue", "#2c5fa6"],
  ["red", "#b5232f"],
];

const FALLBACK_GARMENT = "#efe7d8"; // soft unbleached

function normaliseHex(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let s = raw.trim().toLowerCase();
  if (!s) return null;
  if (!s.startsWith("#")) s = "#" + s;
  if (/^#[0-9a-f]{3}$/.test(s)) {
    s = "#" + s.slice(1).split("").map((c) => c + c).join("");
  }
  return /^#[0-9a-f]{6}$/.test(s) ? s : null;
}

export function resolveGarmentHex(hex: string | null, name: string | null): string {
  const direct = normaliseHex(hex);
  if (direct) return direct;
  if (name) {
    const lower = name.toLowerCase();
    for (const [key, val] of COLOUR_NAME_MAP) {
      if (lower.includes(key)) return val;
    }
  }
  return FALLBACK_GARMENT;
}

export function isDarkColour(hex: string): boolean {
  const m = hex.replace("#", "");
  const toLin = (v: number) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  const r = toLin(parseInt(m.slice(0, 2), 16) / 255);
  const g = toLin(parseInt(m.slice(2, 4), 16) / 255);
  const b = toLin(parseInt(m.slice(4, 6), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.42;
}

// ── Layout maths ───────────────────────────────────────────────────────
// All in mm, centred on the canvas. The size slider value is the LONGEST
// side of the whole design.

export type ArtBox = { cx: number; cy: number; w: number; h: number };
export type TextBox = { cx: number; cy: number; w: number; fontMm: number };
export type Layout = {
  blockW: number;
  blockH: number;
  art: ArtBox | null;
  text: TextBox | null;
};

// Artwork dimensions for a given longest-side length + aspect ratio.
function artDims(longest: number, aspect: number): { w: number; h: number } {
  return aspect >= 1
    ? { w: longest, h: longest / aspect }
    : { w: longest * aspect, h: longest };
}

// Rough natural rendered width of a single line of text at a cap height.
function naturalTextW(fontMm: number, len: number): number {
  return fontMm * 0.62 * Math.max(1, len);
}

// Wider per-character budget for the varsity / college caps. That face
// is a wide slab set in all-caps, so the generic estimate above sizes
// the font too large and the arched word overlaps itself. This budgets
// extra width per glyph (and tracking) so the letters always spread.
function varsityTextW(fontMm: number, len: number): number {
  return fontMm * 0.92 * Math.max(1, len);
}

// Placement-shape constraints. An "arm" placement runs a long narrow
// strip down the arm, so its design is shown rotated 90deg. (The
// separate "sleeve" / cuff placement is a normal small area — no
// special handling.) A hat front is a short band across the front.
const ARM_MAX_LEN = 220; // mm, down the arm
const ARM_MAX_W = 50; // mm, across the arm
const HAT_MAX_H = 60; // mm, hat band height

// Scale a finished layout uniformly toward the canvas centre — used to
// clamp a design into a placement's physical envelope (sleeve / hat).
function scaleLayout(L: Layout, f: number): Layout {
  if (f >= 1 || !Number.isFinite(f)) return L;
  const cx = CANVAS_MM_W / 2;
  const cy = CANVAS_MM_H / 2;
  const at = (v: number, c: number) => c + (v - c) * f;
  return {
    blockW: L.blockW * f,
    blockH: L.blockH * f,
    art: L.art
      ? {
          cx: at(L.art.cx, cx),
          cy: at(L.art.cy, cy),
          w: L.art.w * f,
          h: L.art.h * f,
        }
      : null,
    text: L.text
      ? {
          cx: at(L.text.cx, cx),
          cy: at(L.text.cy, cy),
          w: L.text.w * f,
          fontMm: L.text.fontMm * f,
        }
      : null,
  };
}

// The upward rise (in mm) of the varsity / college arched text. Shared by
// computeLayout (to reserve headroom above the design) and ArchedText (to
// draw the curve) so the two never disagree. A gentle bow, not a tight
// semicircle — flat enough to read cleanly across the top of the design.
function archBow(textW: number, fontMm: number): number {
  return Math.min(textW * 0.14, fontMm * 1.15);
}

export function computeLayout(p: {
  hasText: boolean;
  hasArt: boolean;
  isPdf: boolean;
  imgAspect: number | null;
  sizeMm: number;
  arrangement: string | null;
  textLen: number;
  /** Varsity / college style — bigger text, tighter gap (rendered as an
   *  arch over the design). */
  varsity?: boolean;
}): Layout {
  const cx0 = CANVAS_MM_W / 2;
  const cy0 = CANVAS_MM_H / 2;
  const size = p.sizeMm;
  // PDF has no measurable aspect — treat the ghost placeholder as square.
  const aspect = p.isPdf
    ? 1
    : p.imgAspect && p.imgAspect > 0
      ? p.imgAspect
      : 1;
  const len = Math.max(1, p.textLen);

  // Text only — the text IS the design; it spans the chosen width.
  if (p.hasText && !p.hasArt) {
    const fontMm = clamp(size / (len * 0.62), size * 0.14, size * 0.72);
    return {
      blockW: size,
      blockH: fontMm,
      art: null,
      text: { cx: cx0, cy: cy0, w: size, fontMm },
    };
  }

  // Design only — longest side = chosen size, true aspect kept.
  if (p.hasArt && !p.hasText) {
    const d = artDims(size, aspect);
    return {
      blockW: d.w,
      blockH: d.h,
      art: { cx: cx0, cy: cy0, w: d.w, h: d.h },
      text: null,
    };
  }

  // Design + text — one combined unit. Lay it out provisionally with the
  // artwork's longest side = size, then scale the WHOLE block so its
  // longest side = size. The text is a caption sized off the artwork, so
  // it can never grow larger than the design.
  const arr = p.arrangement ?? "below";

  if (arr === "above" || arr === "below") {
    const ad = artDims(size, aspect);
    // Text ~ a third of the artwork's height. If the line would run
    // wider than the artwork, scale the FONT down so it keeps its
    // proportions rather than squishing to fit.
    // Varsity caps need a wider width budget so the arched word never
    // overlaps; standard text uses the generic estimate.
    const widthFn = p.varsity ? varsityTextW : naturalTextW;
    let fontMm = ad.h * (p.varsity ? 0.5 : 0.34);
    const nat = widthFn(fontMm, len);
    if (nat > ad.w) fontMm = (fontMm * ad.w) / nat;
    const gap = size * (p.varsity ? 0.04 : 0.05);
    const textW = widthFn(fontMm, len);

    if (p.varsity) {
      // Varsity text is arched, so its row has to reserve the arch's
      // rise plus the glyph cap height — otherwise the curve crowds the
      // top edge of the canvas. We size a taller "text row" and place
      // the baseline so the arched word sits just inside it.
      const bow = archBow(textW, fontMm);
      const ascent = fontMm * 0.82; // glyph top above the arc apex
      const rowH = bow + ascent + fontMm * 0.2;
      const k = size / Math.max(ad.w, ad.h + gap + rowH);
      const aw = ad.w * k;
      const ah = ad.h * k;
      const fm = fontMm * k;
      const g = gap * k;
      const tw = textW * k;
      const rH = rowH * k;
      const blockH = ah + g + rH;
      const top = cy0 - blockH / 2;
      const rowTop = arr === "above" ? top : top + ah + g;
      const artCy = arr === "above" ? top + rH + g + ah / 2 : top + ah / 2;
      const textCy = rowTop + ascent * k + (bow * k) / 2;
      return {
        blockW: aw,
        blockH,
        art: { cx: cx0, cy: artCy, w: aw, h: ah },
        text: { cx: cx0, cy: textCy, w: tw, fontMm: fm },
      };
    }

    const k = size / Math.max(ad.w, ad.h + gap + fontMm);
    const aw = ad.w * k;
    const ah = ad.h * k;
    const fm = fontMm * k;
    const g = gap * k;
    const tw = textW * k;
    const blockH = ah + g + fm;
    const top = cy0 - blockH / 2;
    const artCy = arr === "above" ? top + fm + g + ah / 2 : top + ah / 2;
    const textCy = arr === "above" ? top + fm / 2 : top + ah + g + fm / 2;
    return {
      blockW: aw,
      blockH,
      art: { cx: cx0, cy: artCy, w: aw, h: ah },
      text: { cx: cx0, cy: textCy, w: tw, fontMm: fm },
    };
  }

  // Side by side. Text up to half the artwork's height; if the line
  // would run wider than its column, scale the FONT down (keep the
  // proportions) instead of squishing the glyphs.
  const ad = artDims(size, aspect);
  let fontMm = ad.h * 0.5;
  const maxTextW = size * 0.5;
  const nat = naturalTextW(fontMm, len);
  if (nat > maxTextW) fontMm = (fontMm * maxTextW) / nat;
  const gap = size * 0.05;
  const textW = naturalTextW(fontMm, len);
  const k = size / Math.max(ad.w + gap + textW, ad.h, fontMm);
  const aw = ad.w * k;
  const ah = ad.h * k;
  const fm = fontMm * k;
  const g = gap * k;
  const tw = textW * k;
  const blockW = aw + g + tw;
  const left = cx0 - blockW / 2;
  const artCx = arr === "left" ? left + tw + g + aw / 2 : left + aw / 2;
  const textCx = arr === "left" ? left + tw / 2 : left + aw + g + tw / 2;
  return {
    blockW,
    blockH: Math.max(ah, fm),
    art: { cx: artCx, cy: cy0, w: aw, h: ah },
    text: { cx: textCx, cy: cy0, w: tw, fontMm: fm },
  };
}

// ── Main component ─────────────────────────────────────────────────────

export function PreviewCanvas({
  config,
  spot,
  workerUrl,
  garmentColourHex,
  garmentColourName,
  active,
  onToggleRemoveBg,
}: {
  config: Config;
  /** The spot being edited (live draft), or the most recent saved spot,
   *  or null when nothing has been started yet. */
  spot: EmbroideryDraft | null;
  workerUrl: string;
  garmentColourHex: string | null;
  garmentColourName: string | null;
  /** True while the preview drawer is open. Heavy work (AI background
   *  removal) is deferred until then so a closed drawer costs nothing. */
  active: boolean;
  /** 2026-07-03: the "Remove background" toggle moved OUT of the upload
   *  step (where its wall of caveat text read as friction) and onto the
   *  preview itself - the only place the effect is even visible. Provided
   *  only while a live draft is being edited; absent = no toggle. */
  onToggleRemoveBg?: (on: boolean) => void;
}) {
  const stageRef = React.useRef<HTMLDivElement>(null);
  const [stagePx, setStagePx] = React.useState<{ w: number; h: number }>({
    w: 480,
    h: 508,
  });

  React.useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setStagePx({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const garmentHex = resolveGarmentHex(garmentColourHex, garmentColourName);
  const dark = isDarkColour(garmentHex);

  // W4 phase 2: the artwork pipeline (remove-bg preview, drawing filter,
  // varsity flags, font/threads, measured aspect) lives in the shared
  // hook - identical behaviour to when it was inline here.
  const {
    placement,
    sizeMm,
    displayText,
    isPdf,
    imageUrl,
    removeBg,
    bgBusy,
    finalArtUrl,
    imgAspect,
    drawingNote,
    isVarsity,
    hasText,
    hasArt,
    isEmpty,
    font,
    textColour,
    outlineColour,
    wordArtPreset,
  } = usePreviewArtwork({ config, spot, workerUrl, active, dark });

  // Placement-shape handling. An "arm" placement runs a long narrow
  // strip down the arm (shown rotated 90deg, clamped to 220 x 50mm); a
  // hat front is capped to 60mm tall. The separate "sleeve" / cuff
  // placement is a normal small area and gets no special treatment.
  const placementText = `${placement?.slug ?? ""} ${placement?.view ?? ""} ${
    placement?.name ?? ""
  }`.toLowerCase();
  const garmentText = `${config.garmentType?.kind ?? ""} ${
    config.garmentType?.slug ?? ""
  } ${config.garmentType?.label ?? ""}`.toLowerCase();
  const isArm = /\barm\b/.test(placementText);
  const isHat =
    !isArm &&
    (/\bhat\b|\bcap\b|beanie|bucket|headwear|trucker/.test(garmentText) ||
      /\bhat\b|\bcap\b|beanie/.test(placementText));

  const layout = (() => {
    if (isEmpty) return null;
    let L = computeLayout({
      hasText,
      hasArt,
      isPdf,
      imgAspect,
      sizeMm,
      arrangement: isVarsity ? "above" : spot?.arrangement ?? null,
      textLen: displayText.length,
      varsity: isVarsity,
    });
    if (isArm) {
      // After a 90deg rotation the footprint is blockH wide x blockW tall.
      L = scaleLayout(
        L,
        Math.min(1, ARM_MAX_W / L.blockH, ARM_MAX_LEN / L.blockW),
      );
    } else if (isHat) {
      L = scaleLayout(L, Math.min(1, HAT_MAX_H / L.blockH));
    }
    return L;
  })();

  const placementNote = isArm
    ? "Down-the-arm embroidery — up to 220 x 50mm, shown rotated to match."
    : isHat
      ? "Hat embroidery sits across the front — up to 60mm tall."
      : null;

  // Phase 91: sleeve emoji motifs overlaid on the preview. Pulled from the
  // spot's extraAddonIds -> the sleeve_emoji_* upsell glyphs. Sleeve
  // placements only; emojis sit just below the design block as an accent.
  const emojiGlyphs = isSleeveSlug(spot?.placementSlug ?? placement?.slug ?? null)
    ? (spot?.extraAddonIds ?? [])
        .map((id) => config.upsells?.find((u) => u.id === id)?.appendGlyph)
        .filter((g): g is string => !!g)
    : [];
  const emojiRow = (() => {
    if (emojiGlyphs.length === 0) return null;
    let bottom = CANVAS_MM_H / 2;
    if (layout?.text) {
      bottom = Math.max(bottom, layout.text.cy + (layout.text.fontMm ?? 0) / 2);
    }
    if (layout?.art) {
      bottom = Math.max(bottom, layout.art.cy + (layout.art.h ?? 0) / 2);
    }
    const fontMm = 18;
    const cy = Math.min(CANVAS_MM_H - fontMm / 2, bottom + fontMm);
    return { glyphs: emojiGlyphs.join("  "), cx: CANVAS_MM_W / 2, cy, fontMm };
  })();

  // Adaptive zoom — when the design is small (e.g. a 90mm left chest)
  // the preview zooms in so it reads clearly, then eases back out as the
  // design grows. The ruler + size badge keep reporting true mm; only
  // the visible window scales.
  const ZOOM_MIN = 0.4;
  const ZOOM_FILL = 0.62; // fraction of the window the design fills
  const fpW = layout ? (isArm ? layout.blockH : layout.blockW) : CANVAS_MM_W;
  const fpH = layout ? (isArm ? layout.blockW : layout.blockH) : CANVAS_MM_H;
  const targetZoom = layout
    ? Math.min(
        1,
        Math.max(
          ZOOM_MIN,
          fpW / (CANVAS_MM_W * ZOOM_FILL),
          fpH / (CANVAS_MM_H * ZOOM_FILL),
        ),
      )
    : 1;
  const zoomAnim = React.useRef(1);
  const [, bumpZoom] = React.useState(0);
  React.useEffect(() => {
    let raf = 0;
    const loop = () => {
      const cur = zoomAnim.current;
      const diff = targetZoom - cur;
      if (Math.abs(diff) < 0.004) {
        zoomAnim.current = targetZoom;
        bumpZoom((n) => n + 1);
        return;
      }
      zoomAnim.current = cur + diff * 0.2;
      bumpZoom((n) => n + 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [targetZoom]);
  const displayZoom = zoomAnim.current;
  const winW = CANVAS_MM_W * displayZoom;
  const winH = CANVAS_MM_H * displayZoom;
  const winX = (CANVAS_MM_W - winW) / 2;
  const winY = (CANVAS_MM_H - winH) / 2;
  const mmPx = stagePx.w / winW;

  const stageStyle = {
    background: garmentHex,
    ["--rk-pc-pv-dot" as string]: dark
      ? "rgba(255,255,255,0.26)"
      : "rgba(17,17,17,0.17)",
    ["--rk-pc-pv-cross" as string]: dark
      ? "rgba(255,255,255,0.20)"
      : "rgba(17,17,17,0.12)",
  } as React.CSSProperties;

  return (
    <div className="rk-pc-pv-canvas">
      <div className="rk-pc-pv-meta">
        <span className="rk-pc-pv-meta__eyebrow">Live preview</span>
        <span className="rk-pc-pv-meta__placement">
          <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true">
            <circle cx="5" cy="5" r="3.6" fill="currentColor" />
          </svg>
          {placement ? placement.name : "Pick a placement"}
        </span>
        <span className="rk-pc-pv-meta__spacer" />
        <span className="rk-pc-pv-meta__size">
          <strong>{spot?.sizeMm || 0}</strong>
          <span>mm</span>
        </span>
      </div>

      <div
        className="rk-pc-pv-frame"
        style={
          { ["--rk-pc-pv-ruler" as string]: `${RULER_PX}px` } as React.CSSProperties
        }
      >
        <div className="rk-pc-pv-frame__corner">
          <span>mm</span>
        </div>
        <div className="rk-pc-pv-frame__ruler-top" aria-hidden="true">
          <Ruler
            axis="x"
            totalMm={winW}
            mmPx={mmPx}
            highlightMm={
              layout ? (isArm ? layout.blockH : layout.blockW) : 0
            }
          />
        </div>
        <div className="rk-pc-pv-frame__ruler-left" aria-hidden="true">
          <Ruler axis="y" totalMm={winH} mmPx={mmPx} highlightMm={0} />
        </div>

        <div className="rk-pc-pv-stage" ref={stageRef} style={stageStyle}>
          <div
            className="rk-pc-pv-stage__grid"
            aria-hidden="true"
            style={{
              backgroundSize: `${(10 / winW) * 100}% ${(10 / winH) * 100}%`,
            }}
          />

          <svg
            className="rk-pc-pv-stage__svg"
            viewBox={`${winX} ${winY} ${winW} ${winH}`}
            preserveAspectRatio="xMidYMid meet"
            aria-hidden={isEmpty}
          >
            <PreviewDefs />
            <line
              className="rk-pc-pv-cross"
              x1={CANVAS_MM_W / 2}
              y1={0}
              x2={CANVAS_MM_W / 2}
              y2={CANVAS_MM_H}
            />
            <line
              className="rk-pc-pv-cross"
              x1={0}
              y1={CANVAS_MM_H / 2}
              x2={CANVAS_MM_W}
              y2={CANVAS_MM_H / 2}
            />
            <g
              transform={
                isArm
                  ? `rotate(90 ${CANVAS_MM_W / 2} ${CANVAS_MM_H / 2})`
                  : undefined
              }
            >
              {layout?.art ? (
                <LayoutArt
                  box={layout.art}
                  imageUrl={finalArtUrl}
                  isPdf={isPdf}
                  filename={spot?.uploadFilename ?? null}
                />
              ) : null}
              {layout?.text ? (
                wordArtPreset ? (
                  <WordArtText
                    box={layout.text}
                    text={displayText}
                    preset={wordArtPreset}
                    colour={textColour}
                  />
                ) : isVarsity ? (
                  <ArchedText
                    box={layout.text}
                    text={displayText}
                    font={font}
                    colour={textColour}
                    strokeColour={outlineColour}
                  />
                ) : (
                  <LayoutText
                    box={layout.text}
                    text={displayText}
                    font={font}
                    colour={textColour}
                  />
                )
              ) : null}
              {emojiRow ? (
                <text
                  x={emojiRow.cx}
                  y={emojiRow.cy}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={emojiRow.fontMm}
                  style={{ userSelect: "none" }}
                >
                  {emojiRow.glyphs}
                </text>
              ) : null}
            </g>
          </svg>

          {isEmpty ? <EmptyState /> : null}

          {bgBusy ? (
            <div className="rk-pc-pv-working" aria-hidden="true">
              <span className="rk-pc-pv-working__spinner" />
              Removing background… (give it a second)
            </div>
          ) : null}

          {layout ? (
            <SizeBadge
              widthMm={isArm ? layout.blockH : layout.blockW}
              heightMm={isArm ? layout.blockW : layout.blockH}
              mmPx={mmPx}
            />
          ) : null}
        </div>
      </div>

      {placementNote ? (
        <div className="rk-pc-pv-note">{placementNote}</div>
      ) : null}
      {drawingNote ? (
        <div className="rk-pc-pv-note">{drawingNote}</div>
      ) : null}

      {/* Remove-background toggle lives ON the preview (2026-07-03): the
          effect is visual-only, so the control belongs where the visual is.
          Only offered while editing a draft with a non-PDF upload. */}
      {onToggleRemoveBg &&
        imageUrl &&
        !(spot?.uploadMimeType ?? "").toLowerCase().includes("pdf") &&
        !(spot?.uploadFilename ?? "").toLowerCase().endsWith(".pdf") && (
          <label className="rk-pc-pv-bgtoggle">
            <input
              type="checkbox"
              checked={removeBg}
              onChange={(e) => onToggleRemoveBg(e.target.checked)}
            />
            <span className="rk-pc-pv-bgtoggle__label">
              Remove background
              {removeBg && bgBusy && (
                <span className="rk-pc-pv-bgtoggle__busy"> processing…</span>
              )}
            </span>
          </label>
        )}

      <div className="rk-pc-pv-legend">
        <span className="rk-pc-pv-legend__item">
          <span
            className="rk-pc-pv-legend__swatch"
            style={{ background: garmentHex }}
          />
          {garmentColourName ? `${garmentColourName} garment` : "Garment colour"}
        </span>
        <span className="rk-pc-pv-legend__spacer" />
        <span className="rk-pc-pv-legend__item rk-pc-pv-legend__item--muted">
          A visual guide, not 100% accurate
        </span>
      </div>
    </div>
  );
}

// ── Ruler ──────────────────────────────────────────────────────────────

function Ruler({
  axis,
  totalMm,
  mmPx,
  highlightMm,
}: {
  axis: "x" | "y";
  totalMm: number;
  mmPx: number;
  highlightMm: number;
}) {
  const ticks: React.ReactNode[] = [];
  for (let mm = 0; mm <= totalMm; mm += 10) {
    const isMajor = mm % 50 === 0;
    // Label only every 50mm — every 20 was too crowded.
    const isLabel = mm % 50 === 0 && mm > 0;
    const pos =
      axis === "x" ? { left: `${mm * mmPx}px` } : { top: `${mm * mmPx}px` };
    const cls = [
      "rk-pc-pv-tick",
      `rk-pc-pv-tick--${axis}`,
      isMajor ? "is-major" : "is-minor",
    ].join(" ");
    ticks.push(
      <span key={mm} className={cls} style={pos}>
        {isLabel ? <small>{mm}</small> : null}
      </span>,
    );
  }

  let highlight: React.ReactNode = null;
  if (axis === "x" && highlightMm > 0) {
    const startMm = (totalMm - highlightMm) / 2;
    highlight = (
      <span
        className="rk-pc-pv-ruler__hl"
        style={{
          left: `${startMm * mmPx}px`,
          width: `${highlightMm * mmPx}px`,
        }}
      >
        <span className="rk-pc-pv-ruler__hl-label">{Math.round(highlightMm)}mm</span>
      </span>
    );
  }

  return (
    <div className={`rk-pc-pv-ruler rk-pc-pv-ruler--${axis}`}>
      {highlight}
      {ticks}
    </div>
  );
}

// ── Artwork ────────────────────────────────────────────────────────────

export function LayoutArt({
  box,
  imageUrl,
  isPdf,
  filename,
}: {
  box: ArtBox;
  imageUrl: string | null;
  isPdf: boolean;
  filename: string | null;
}) {
  if (isPdf) {
    return (
      <PdfPlaceholder
        cx={box.cx}
        cy={box.cy}
        widthMm={box.w}
        heightMm={box.h}
        filename={filename}
      />
    );
  }
  if (!imageUrl) return null;
  return (
    <image
      href={imageUrl}
      x={box.cx - box.w / 2}
      y={box.cy - box.h / 2}
      width={box.w}
      height={box.h}
      preserveAspectRatio="xMidYMid meet"
    />
  );
}

// ── Text ───────────────────────────────────────────────────────────────

export function LayoutText({
  box,
  text,
  font,
  colour,
}: {
  box: TextBox;
  text: string;
  font: Font | null;
  colour: string;
}) {
  const ref = React.useRef<SVGTextElement>(null);
  const [scale, setScale] = React.useState(1);
  // Split into non-empty lines so a customer typing name + date stacks
  // vertically. SVG <text> ignores \n on its own — each line becomes a
  // <tspan> with x=0 + dy=1em so they cascade.
  const lines = text.split("\n").map((ln) => ln.trim()).filter(Boolean);
  const trimmed = lines.join("\n");
  const lineCount = Math.max(1, lines.length);
  const lineHeightEm = 1.05;

  // Render the text at its natural proportions, measure the WIDEST line,
  // then scale the whole block uniformly. This fills the width without
  // distorting glyphs (no textLength squashing).
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !trimmed || box.w <= 0) return;
    const measure = () => {
      let maxNat = 0;
      try {
        // Each line is its own <tspan> — measure them individually so we
        // scale by the WIDEST line, not the concatenated string.
        for (let i = 0; i < el.children.length; i++) {
          const child = el.children[i] as SVGTSpanElement;
          const w = child.getComputedTextLength
            ? child.getComputedTextLength()
            : 0;
          if (w > maxNat) maxNat = w;
        }
        if (maxNat === 0) maxNat = el.getComputedTextLength();
      } catch {
        maxNat = 0;
      }
      if (maxNat > 0) {
        setScale(Math.max(0.25, Math.min(2.4, box.w / maxNat)));
      }
    };
    measure();
    // Re-measure once the real embroidery webfont has loaded — the
    // natural width changes when it swaps in for the fallback face.
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
  }, [trimmed, box.w, box.fontMm, font?.family, font?.tracking, lineCount]);

  if (!trimmed) return null;
  const family = font
    ? `'${font.family}', 'Hanken Grotesk', system-ui, sans-serif`
    : "'Hanken Grotesk', system-ui, sans-serif";
  // Vertical centring: the block's total height is approximately
  // (lineCount - 1) * lineHeightEm + 1. We bias the first line up so the
  // stack reads as centred around box.cy.
  const firstLineDy = box.fontMm * (0.34 - ((lineCount - 1) * lineHeightEm) / 2);
  return (
    <g transform={`translate(${box.cx} ${box.cy}) scale(${scale})`}>
      <text
        ref={ref}
        x={0}
        y={firstLineDy}
        textAnchor="middle"
        dominantBaseline="alphabetic"
        fontFamily={family}
        fontSize={box.fontMm}
        fill={colour}
        letterSpacing={font?.tracking || undefined}
        filter="url(#rk-pc-pv-stitch)"
      >
        {lines.map((ln, i) => (
          <tspan
            key={i}
            x={0}
            dy={i === 0 ? 0 : `${lineHeightEm}em`}
          >
            {ln}
          </tspan>
        ))}
      </text>
    </g>
  );
}

// ── Arched text (varsity / college style) ──────────────────────────────
// Renders the text along an upward-bowing arc so it wraps over the top
// of the design like a college crest.

export function ArchedText({
  box,
  text,
  font,
  colour,
  strokeColour,
}: {
  box: TextBox;
  text: string;
  font: Font | null;
  colour: string;
  /** Thread colour for the stitched outline around the text, or null
   *  when the customer hasn't picked one yet (no stroke is drawn). */
  strokeColour: string | null;
}) {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const halfW = box.w / 2;
  const bow = archBow(box.w, box.fontMm);
  const ends = box.cy + bow / 2; // y of the arc endpoints
  const apexCtrl = ends - bow * 2; // quadratic control point
  // Rough length of the quadratic arc — used so the text fills it.
  const ctrlLeg = Math.sqrt(halfW * halfW + 4 * bow * bow);
  const pathLen = (2 * box.w + 2 * ctrlLeg) / 3;
  const family = font
    ? `'${font.family}', 'Hanken Grotesk', system-ui, sans-serif`
    : "'Hanken Grotesk', system-ui, sans-serif";
  // Outline drawn behind the fill (paint-order: stroke) so it reads as a
  // crisp border without eating into the letterforms.
  const strokeW = strokeColour ? box.fontMm * 0.09 : 0;

  return (
    <g>
      <defs>
        <path
          id="rk-pc-pv-arc"
          fill="none"
          d={`M ${box.cx - halfW} ${ends} Q ${box.cx} ${apexCtrl} ${
            box.cx + halfW
          } ${ends}`}
        />
      </defs>
      <text
        fontFamily={family}
        fontSize={box.fontMm}
        fill={colour}
        stroke={strokeColour ?? undefined}
        strokeWidth={strokeW || undefined}
        strokeLinejoin="round"
        paintOrder="stroke"
        letterSpacing={font?.tracking || undefined}
        filter="url(#rk-pc-pv-stitch)"
      >
        <textPath
          href="#rk-pc-pv-arc"
          textLength={pathLen}
          lengthAdjust="spacing"
        >
          {text}
        </textPath>
      </text>
    </g>
  );
}

// ── PDF placeholder ────────────────────────────────────────────────────

function PdfPlaceholder({
  cx,
  cy,
  widthMm,
  heightMm,
  filename,
}: {
  cx: number;
  cy: number;
  widthMm: number;
  heightMm: number;
  filename: string | null;
}) {
  const x = cx - widthMm / 2;
  const y = cy - heightMm / 2;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={widthMm}
        height={heightMm}
        fill="rgba(255,255,255,0.5)"
        stroke="#7c3f71"
        strokeWidth={0.7}
        strokeDasharray="2.4 1.8"
        rx={2}
      />
      <text
        x={cx}
        y={cy - 3}
        textAnchor="middle"
        fontFamily="'Hanken Grotesk', system-ui, sans-serif"
        fontSize={7}
        fontWeight={700}
        fill="#5c1756"
      >
        PDF
      </text>
      <text
        x={cx}
        y={cy + 6}
        textAnchor="middle"
        fontFamily="'Hanken Grotesk', system-ui, sans-serif"
        fontSize={4}
        fill="#6b7280"
      >
        {filename || "uploaded.pdf"}
      </text>
      <text
        x={cx}
        y={cy + 13}
        textAnchor="middle"
        fontFamily="'Hanken Grotesk', system-ui, sans-serif"
        fontSize={3.4}
        fill="#9ca3af"
      >
        Shown at {Math.round(widthMm)} x {Math.round(heightMm)} mm
      </text>
    </g>
  );
}

// ── Stitch filter ──────────────────────────────────────────────────────

export function PreviewDefs() {
  return (
    <defs>
      <filter id="rk-pc-pv-stitch" x="-6%" y="-6%" width="112%" height="112%">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="1.5"
          numOctaves={2}
          seed={4}
          result="noise"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="noise"
          scale="0.22"
          result="rough"
        />
        <feMorphology
          operator="dilate"
          radius="0.07"
          in="rough"
          result="thick"
        />
        <feGaussianBlur stdDeviation="0.1" in="thick" result="soft" />
        <feFlood floodColor="rgba(0,0,0,0.3)" result="shadowColor" />
        <feComposite
          in="shadowColor"
          in2="soft"
          operator="in"
          result="shadow"
        />
        <feOffset dx="0" dy="0.5" in="shadow" result="shadowOff" />
        <feMerge>
          <feMergeNode in="shadowOff" />
          <feMergeNode in="soft" />
        </feMerge>
      </filter>
    </defs>
  );
}

// ── Empty state ────────────────────────────────────────────────────────

export function EmptyState() {
  return (
    <div className="rk-pc-pv-empty">
      <div className="rk-pc-pv-empty__card">
        <img
          className="rk-pc-pv-empty__logo"
          src="https://cdn.shopify.com/s/files/1/0434/4830/3779/files/Easy_Peasy_Embroider-05_x320.png?v=1655015445"
          alt="RK Threads"
        />
        <strong>Your preview shows up here</strong>
        <span>
          Start customising below for this preview to start to update…
        </span>
      </div>
    </div>
  );
}

// ── Size badge ─────────────────────────────────────────────────────────

function SizeBadge({
  widthMm,
  heightMm,
  mmPx,
}: {
  widthMm: number;
  heightMm: number;
  mmPx: number;
}) {
  return (
    <div className="rk-pc-pv-sizebadge" aria-hidden="true">
      <span className="rk-pc-pv-sizebadge__line">
        <span className="rk-pc-pv-sizebadge__cap" />
        <span
          className="rk-pc-pv-sizebadge__bar"
          style={{ width: `${Math.max(0, widthMm * mmPx)}px` }}
        />
        <span className="rk-pc-pv-sizebadge__cap" />
      </span>
      <span className="rk-pc-pv-sizebadge__text">
        <strong>{Math.round(widthMm)}</strong> x{" "}
        <strong>{Math.round(heightMm)}</strong> mm
      </span>
    </div>
  );
}
